import { PropTypes } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { Typography, Tooltip, IconButton, Card, Button, CardContent, Grid, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useGetOrganizationUserQuery } from '../features/api/rest/apiOrganizationsSlice';
import { useDestroyOrganizationBrandMutation } from '../features/api/rest/apiBrandOrganizationSlice';
import { useGetAlternativeNamesQuery } from '../features/api/rest/apiBrandAlternativeNamesSlice';
import { useGetProfileQuery } from '../features/api/rest/apiProfileSlice';
import BrandOrganizationDialog from './BrandOrganizationDialog';

function BrandCard({ id, name }) {
  const { opk } = useParams();
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteBrand] = useDestroyOrganizationBrandMutation();
  const { data: profile } = useGetProfileQuery();
  const { data: users = [] } = useGetOrganizationUserQuery(opk);
  const { data: alternativeName = [] } = useGetAlternativeNamesQuery({ opk, id });

  let SystemAdmin = false;
  let OrgAdmin = false;
  const permission = () => {
    if (profile.is_staff) {
      SystemAdmin = true;
    }
    OrgAdmin = users.some((user) => user.is_admin && profile.id === user.user);
    if (OrgAdmin || SystemAdmin) return true;
    return false;
  };
  const handleDelete = async () => {
    try {
      await deleteBrand({ opk, id });
    } finally {
      setIsDeleting(false);
    }
  };
  return (
    <Card className="Brand_Card">
      <CardContent sx={{ p: 1 }}>
        <Grid sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Grid key="nomeBrand">
            <Typography
              variant="h7"
              sx={{
                paddingTop: '0.5em',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: '1',
                WebkitBoxOrient: 'vertical',
                textAlign: 'initial',
                overflow: 'hidden',
                wordBreak: 'break-word',
              }}
            >
              {name}
            </Typography>
            <Grid
              sx={{
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: '2',
                WebkitBoxOrient: 'vertical',
                textAlign: 'initial',
                overflow: 'hidden',
                wordBreak: 'break-word',
                minHeight: '3em',
              }}
              key="NomiAlternativi"
            >
              {alternativeName &&
                alternativeName.map((altname) => (
                  <Typography variant="h8" key={altname.id} sx={{ fontSize: '0.9em', pr: '0.6em', color: '#47484e', fontStyle: 'italic' }}>
                    {altname.name}
                  </Typography>
                ))}
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
      <Grid>
        {permission() && (
          <Grid container alignItems="center" mt={1} sx={{ gap: 2, display: 'flex', justifyContent: 'space-evenly' }}>
            <Grid item>
              <Tooltip title={t('common.actions.edit')} placement="top">
                <IconButton onClick={() => setIsEditing(true)}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid item>
              <Tooltip title={t('common.actions.delete')} placement="top">
                <IconButton onClick={() => setIsDeleting(true)}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        )}
      </Grid>
      <Dialog open={isDeleting} onClose={() => setIsDeleting(false)}>
        <DialogTitle sx={{ textAlign: 'center' }}>{t('organizations.brand.delete')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" mx={4}>
            {t('organizations.brand.deleteMessage')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ pt: 0, pb: 3, px: 3 }}>
          <Grid display="flex" justifyContent="flex-end" gap={1}>
            <Button type="submit" onClick={() => setIsDeleting(false)} variant="outlined" color="secondary">
              {t('common.actions.close')}
            </Button>
            <Button type="submit" onClick={handleDelete} variant="contained" color="warning">
              {t('common.actions.delete')}
            </Button>
          </Grid>
        </DialogActions>
      </Dialog>
      <BrandOrganizationDialog actionType="edit" isOpen={isEditing} setIsOpen={setIsEditing} id={id} oldName={name} />
    </Card>
  );
}

BrandCard.propTypes = {
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
};

export default BrandCard;
